<template>
  <div
    v-if="!ratesLoading"
    class="grid-column-span-4_md flex flex-column justify-items-center vh75">
    <h1>Your workforce</h1>
    <div class="font-size-4 mb3">
      <p>How big is your organization? How big do you want it to be in one year? How much should people make on average?</p>
      <p>You can adjust these values anytime.</p>
    </div>
    <form
      action=""
      class="grid grid-columns-2_sm grid-columns-7_md px0 align-items-end"
      @submit.prevent="editRates">
      <fieldset class="grid-column-start-1 grid-column-end-4_md">
        <div class="my2">
          <h2>Current workforce</h2>
          <p>Who works for you now? And what&rsquo;s the average salary?</p>
        </div>
        <lw-text-input
          v-validate="'required|numeric'"
          ref="cfte"
          :name="'currentFullTimeEmployees'"
          :label="'Full-time employees'"
          :hint="'Total number of full-time employees and contractors'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          v-model.number="currentFullTimeEmployees"
          :error="errors.first('currentFullTimeEmployees')"
        />
        <lw-text-input
          v-validate="'required|numeric'"
          :name="'currentBillableEmployees'"
          :label="'Billable employees'"
          :hint="'Employees and contractors who do work that gets billed to clients'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          :tip="'Add the full-time equivalent in freelancers here if you don‘t pay their expenses. E.g., If you have 3 regular freelancers that work about 33% of their time with your company over a year, count them as one billable employee.'"
          v-model.number="currentBillableEmployees"
          :error="errors.first('currentBillableEmployees')"
        />
        <lw-currency-input
          v-validate="'required|numeric'"
          :name="'currentAverageSalary'"
          :label="'Average annual salary'"
          :hint="'Hint: Skip any outliers and convert contractor rates to equivalent salaries'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          :currency="organization.currency"
          v-model.number="currentAverageSalary"
          :error="errors.first('currentAverageSalary')"
        />
      </fieldset>

      <fieldset class="grid-column-start-5_md grid-column-end-8_md">
        <div class="my2">
          <h2>Ideal workforce</h2>
          <p>How would you like your organization to look a year from now?</p>
        </div>
        <lw-text-input
          v-validate="'required|numeric'"
          :name="'goalFullTimeEmployees'"
          :label="'Full-time employees'"
          :hint="'Total number of full-time employees and contractors'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          v-model.number="goalFullTimeEmployees"
          :error="errors.first('goalFullTimeEmployees')"
        />
        <lw-text-input
          v-validate="'required|numeric'"
          :name="'goalBillableEmployees'"
          :label="'Billable employees'"
          :hint="'Employees and full-time contractors who do work that gets billed to clients'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          :tip="'Add the full-time equivalent in freelancers here if you don‘t pay their expenses. E.g., If you have 3 regular freelancers that work about 33% of their time with your company over a year, count them as one billable employee.'"
          v-model.number="goalBillableEmployees"
          :error="errors.first('goalBillableEmployees')"
        />
        <lw-currency-input
          v-validate="'required|numeric'"
          :name="'goalAverageSalary'"
          :label="'Average annual salary'"
          :hint="'Hint: Skip any outliers and convert contractor rates to equivalent salaries'"
          :align="'right'"
          :size="'large'"
          :type="'number'"
          :currency="organization.currency"
          v-model.number="goalAverageSalary"
          :error="errors.first('goalAverageSalary')"
        />
      </fieldset>
      <div class="flex justify-content-flex-end align-items-center align-baseline mb-margin grid-column-span-2_sm grid-column-span-7_md">
        <!--<router-link :to="{ name: 'newRateCulture', params: { id: this.page } }">Skip</router-link>-->
        <button
          type="submit"
          class="large-button accent-button mr1"
          @click.prevent="editRates">Next</button>
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as firebase from 'firebase'
import TextInput from '../../livewires/TextInput'
import CurrencyInput from '../../livewires/CurrencyInput'

export default {
  components: {
    lwTextInput: TextInput,
    lwCurrencyInput: CurrencyInput
  },
  data () {
    return {
      page: this.$route.params.id,
      currentFullTimeEmployees: '',
      currentBillableEmployees: '',
      currentAverageSalary: 0,
      goalFullTimeEmployees: '',
      goalBillableEmployees: '',
      goalAverageSalary: 0
    }
  },
  computed: {
    ...mapGetters([
      'ratesLoading',
      'organization'
    ]),
    rate () {
      return this.$store.getters.rateCard(this.page)
    }
  },
  mounted () {
    this.rate.current.fullTimeEmployees > 1 ? this.currentFullTimeEmployees = this.rate.current.fullTimeEmployees : this.currentFullTimeEmployees = ''
    this.rate.goal.fullTimeEmployees > 1 ? this.goalFullTimeEmployees = this.rate.goal.fullTimeEmployees : this.goalFullTimeEmployees = ''
    this.rate.current.billableEmployees > 1 ? this.currentBillableEmployees = this.rate.current.billableEmployees : this.currentBillableEmployees = ''
    this.rate.goal.billableEmployees > 1 ? this.goalBillableEmployees = this.rate.goal.billableEmployees : this.goalBillableEmployees = ''
    this.rate.current.averageSalary > 1 ? this.currentAverageSalary = this.rate.current.averageSalary : this.currentAverageSalary = ''
    this.rate.goal.averageSalary > 1 ? this.goalAverageSalary = this.rate.goal.averageSalary : this.goalAverageSalary = ''
    this.$refs.cfte.$el.children[0].focus()
  },
  methods: {
    editRates () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch('saveState', false)
          const updated = firebase.default.firestore.FieldValue.serverTimestamp()
          let payload = { ...this.rate }
          payload.updated = updated
          payload.current.fullTimeEmployees = this.currentFullTimeEmployees
          payload.goal.fullTimeEmployees = this.goalFullTimeEmployees
          payload.current.billableEmployees = this.currentBillableEmployees
          payload.goal.billableEmployees = this.goalBillableEmployees
          payload.current.averageSalary = this.currentAverageSalary
          payload.goal.averageSalary = this.goalAverageSalary
          payload.id = this.page
          this.$store.dispatch('editRates', payload).then(response => {
            this.$router.push({ name: 'newRateCulture', params: { id: this.page } })
          })
        }
      })
    }
  }
}
</script>
